// --- START OF FILE product-info.js ---

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      variantData = []; // Stores all variants for the current product
      mediaGallery = undefined;
      // _productDataFromPage can be used to cache product data from the global JSON script
      _productDataFromPage = null;


      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.handleOptionValueChange = this.handleOptionValueChange.bind(this);
        this.fetchQuantityRules = this.fetchQuantityRules.bind(this);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.optionValueSelectionChange) {
            // console.log("DEBUG product-info.js: Subscribing to event name:", PUB_SUB_EVENTS.optionValueSelectionChange);
            this.onVariantChangeUnsubscriber = subscribe(
              PUB_SUB_EVENTS.optionValueSelectionChange,
              this.handleOptionValueChange
            );
        } else {
            console.error("ProductInfo: `subscribe` function or `PUB_SUB_EVENTS.optionValueSelectionChange` not available at connectedCallback.");
        }
        
        const variantDataScript = this.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"]');
        if (variantDataScript) {
          try {
            this.variantData = JSON.parse(variantDataScript.textContent);
            // console.log('[product-info.js] Initialized this.variantData from variant picker script:', JSON.parse(JSON.stringify(this.variantData)));
          } catch (e) {
            console.error("ProductInfo: Failed to parse variant JSON data from variant picker script.", e);
            this.variantData = []; 
          }
        } else {
          // console.warn("ProductInfo: Variant JSON data script within variant-selects/radios not found. `this.variantData` will be empty initially unless populated by product swap.");
          this.variantData = []; 
        }

        this.mediaGallery = this.querySelector('media-gallery');
        this.initializeProductSwapUtility();
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules);
        }
      }

      disconnectedCallback() {
        if (typeof this.onVariantChangeUnsubscriber === 'function') {
            this.onVariantChangeUnsubscriber();
        }
        if (typeof this.cartUpdateUnsubscriber === 'function') {
            this.cartUpdateUnsubscriber();
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, productUrl: eventProductUrl } }) {
        // console.log('%c[product-info.js] handleOptionValueChange TRIGGERED (via pub/sub)', 'color: green; font-weight: bold;');
        // console.log('[product-info.js] Received selectedOptionValues from pub/sub:', selectedOptionValues);

        if (!event || !target || !selectedOptionValues) {
          console.error('[product-info.js] handleOptionValueChange: Missing critical event data.');
          return;
        }
        
        // Ensure this.variantData is populated (it should be from connectedCallback or handleSwapProduct)
        if ((!this.variantData || this.variantData.length === 0) && !(eventProductUrl && this.dataset.url !== eventProductUrl)) {
            console.error('[product-info.js] handleOptionValueChange: this.variantData is empty and not a product swap. Cannot find variant.');
            return; 
        }

        let currentVariant = null;
        if (this.variantData && this.variantData.length > 0) {
            currentVariant = this.variantData.find((variant) => {
                if (!variant.options || variant.options.length !== selectedOptionValues.length) {
                    return false;
                }
                return selectedOptionValues.every((optionValue, index) => {
                    const vOpt = String(variant.options[index] || '').trim().toLowerCase();
                    const sOpt = String(optionValue || '').trim().toLowerCase();
                    return vOpt === sOpt;
                });
            });
        }

        console.log('[product-info.js] Found currentVariant based on selectedOptionValues:', JSON.parse(JSON.stringify(currentVariant || {})));

        if (!currentVariant && !(eventProductUrl && this.dataset.url !== eventProductUrl)) { 
          console.warn('[product-info.js] No matching variant for options:', selectedOptionValues, '. Setting UI to unavailable.');
          this.setUnavailable(); 
          try {
            for (const key in window) {
              if (key.startsWith('updateFeatureIconGrid_') && typeof window[key] === 'function') {
                // console.log('[product-info.js] (No variant match) Calling FIG update function:', key, 'with null variant.');
                window[key](null); 
              }
            }
          } catch (e) { /* ignore */ }
          return; 
        }

        if (currentVariant) {
            const hiddenVariantInput = document.getElementById(this.dataset.formId)?.querySelector('input[name="id"].product-variant-id') || this.querySelector('form input[name="id"].product-variant-id');
            if (hiddenVariantInput) {
                hiddenVariantInput.value = currentVariant.id;
                hiddenVariantInput.disabled = !currentVariant.available;
            }
            if (currentVariant.featured_media && currentVariant.featured_media.id && this.mediaGallery && typeof this.mediaGallery.setActiveMedia === 'function') {
                this.mediaGallery.setActiveMedia(`${this.dataset.section || this.sectionId || this.id.split('__').pop()}-${currentVariant.featured_media.id}`, true);
            }
        }

        this.resetProductFormState();

        const productUrlForFetch = eventProductUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrlForFetch;
        const shouldSwapProduct = this.dataset.url !== productUrlForFetch;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const targetIdForFocus = target ? target.id : (event.target ? event.target.id : '');
        
        // console.log('[product-info.js] currentVariant object being passed to renderProductInfo callbacks:', JSON.parse(JSON.stringify(currentVariant || {})));

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrlForFetch, selectedOptionValues, currentVariant ? currentVariant.id : null, shouldFetchFullPage),
          targetId: targetIdForFocus,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrlForFetch, shouldFetchFullPage, currentVariant) // Pass currentVariant
            : this.handleUpdateProductInfo(productUrlForFetch, currentVariant), // Pass currentVariant
        });
      }

      resetProductFormState() {
        const productForm = this.productForm; 
        if (productForm) { 
            if (typeof productForm.toggleSubmitButton === 'function') {
                productForm.toggleSubmitButton(true, window.variantStrings.addingToCart); 
            } else {
                // console.warn('[product-info.js] resetProductFormState: productForm.toggleSubmitButton is not a function.');
            }
            if (typeof productForm.handleErrorMessage === 'function') {
               productForm.handleErrorMessage(); 
            } else {
                // console.warn('[product-info.js] resetProductFormState: productForm.handleErrorMessage is not a function.');
            }
        } else {
            // console.warn('[product-info.js] resetProductFormState: this.productForm is not available.');
        }
      }

      handleSwapProduct(productUrl, updateFullPage, knownVariant = null) { // MODIFIED: Accept knownVariant
        return (html) => {
          this.productModal?.remove();
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const newProductInfoNode = html.querySelector(selector);

          const variantDataScript = newProductInfoNode?.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"]');
          if (variantDataScript) {
            try {
              this.variantData = JSON.parse(variantDataScript.textContent); // Update this.variantData for the new product
              // console.log('[product-info.js] handleSwapProduct: Re-initialized this.variantData for new product:', JSON.parse(JSON.stringify(this.variantData)));
            } catch (e) { this.variantData = []; }
          } else { this.variantData = []; }

          const variant = knownVariant || this.getSelectedVariant(newProductInfoNode); // Prioritize knownVariant
          // console.log('[product-info.js] handleSwapProduct - Using variant:', JSON.parse(JSON.stringify(variant || {})));
          
          // --- DIRECT CALL TO FEATURE ICON GRID (for product swap) ---
          try {
            for (const key in window) {
              if (key.startsWith('updateFeatureIconGrid_') && typeof window[key] === 'function') {
                // console.log('[product-info.js] (Swap) Calling FIG update function:', key, 'with variant:', JSON.parse(JSON.stringify(variant || {})));
                window[key](variant); 
              }
            }
          } catch (e) { console.error('[product-info.js] (Swap) Error calling FIG global function:', e); }
          // --- END FIG CALL ---

          this.updateURL(productUrl, variant?.id);
          HTMLUpdateUtility.viewTransition(
            updateFullPage ? document.querySelector('main') : this,
            updateFullPage ? html.querySelector('main') : newProductInfoNode,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
          this.mediaGallery = this.querySelector('media-gallery');
        };
      }

      handleUpdateProductInfo(productUrl, knownVariant = null) { // MODIFIED: Accept knownVariant
        return (html) => {
          const newProductInfoNode = html.querySelector(`product-info[data-section-id="${this.dataset.section}"]`) || html.querySelector('product-info');
          const variant = knownVariant || this.getSelectedVariant(newProductInfoNode || html); // PRIORITIZE knownVariant

          // console.log('[product-info.js] handleUpdateProductInfo: Entry point. `knownVariant` received:', JSON.parse(JSON.stringify(knownVariant || {})));
          // console.log('[product-info.js] handleUpdateProductInfo: Final `variant` being used:', JSON.parse(JSON.stringify(variant || {})));

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(newProductInfoNode || html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // --- DIRECT CALL TO FEATURE ICON GRID ---
          try {
            let figFunctionCalled = false;
            // console.log('[product-info.js] About to loop window for FIG functions. Current `variant` (in handleUpdateProductInfo):', JSON.parse(JSON.stringify(variant || {})));
            for (const key in window) {
              if (key.startsWith('updateFeatureIconGrid_') && typeof window[key] === 'function') {
                figFunctionCalled = true;
                // console.log('[product-info.js] Found FIG function:', key, '. Passing current `variant` object:', JSON.parse(JSON.stringify(variant || {})));
                window[key](variant); 
              }
            }
            // if (!figFunctionCalled) {
            //     console.warn('[product-info.js] No FIG update function found on window starting with "updateFeatureIconGrid_".');
            // }
          } catch (e) {
            console.error('[product-info.js] Error trying to call Feature Icon Grid global function:', e);
          }
          // --- END OF DIRECT CALL TO FEATURE ICON GRID ---

          if (!variant) {
            // console.log('[product-info.js] `variant` is null/falsy in handleUpdateProductInfo. Calling setUnavailable() and returning.');
            this.setUnavailable();
            return;
          }

          // console.log('[product-info.js] `variant` is valid in handleUpdateProductInfo. Proceeding. Variant ID:', variant.id);
          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`); 
            const destination = this.querySelector(`#${id}-${this.dataset.section}`); 
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html); 
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          if (this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
            this.productForm.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut
            );
          }
          
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.variantChange) {
            // console.log('[product-info.js] Publishing PUB_SUB_EVENTS.variantChange with variant:', variant);
            publish(PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.sectionId, html, variant },
            });
          }
           
           this.mediaGallery = this.querySelector('media-gallery');
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html); 
          })
          .then(() => { 
            if (targetId && typeof targetId === 'string' && targetId.trim() !== '') {
              try {
                const elementToFocus = document.getElementById(targetId.trim());
                if (elementToFocus) {
                  elementToFocus.focus();
                }
              } catch (e) { /* ignore focus error */ }
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }
      
      getSelectedVariant(productInfoNode) {
        const currentProductNode = productInfoNode || this; // Use provided node or this element

        const variantDataScript = currentProductNode.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"]');
        let localVariantData = this.variantData; // Default to instance's variantData

        if (variantDataScript) { // If script found in the (potentially new) node, prioritize it
            try {
                localVariantData = JSON.parse(variantDataScript.textContent);
            } catch (e) { 
                // console.error("ProductInfo: getSelectedVariant - Error parsing variant data from script tag in provided node.", e);
                // Fallback to instance's variantData if parsing new node fails
                localVariantData = this.variantData;
            }
        }
        
        const formElement = currentProductNode.querySelector('form[data-type="add-to-cart-form"], form[id^="product-form-"]');
        if (!formElement || !localVariantData || localVariantData.length === 0) {
            const selectedVariantJSON = currentProductNode.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]')?.innerHTML;
            return !!selectedVariantJSON ? JSON.parse(selectedVariantJSON) : null;
        }

        const selectedOptions = [];
        // Use this instance's product options order (from productDataFromPage or initial this.variantData.options if available)
        // This assumes product options order doesn't change for the *same* product.
        const productOptionNames = this.productDataFromPage?.options || (this.variantData[0]?.options ? Object.keys(this.variantData[0].options).map((_,i) => this.variantData[0][`option${i+1}`] ? `option${i+1}` : null ).filter(x=>x) : []) ;


        // This part needs to accurately reflect how your options are named/structured in the form
        // Assuming standard option naming pattern: option1, option2, option3 for inputs
        // Or better, if productDataFromPage.options contains the actual option names ['Color', 'Size']
        // and your inputs are named accordingly e.g. name="options[Color]"

        const fieldsets = formElement.querySelectorAll('fieldset.js.product-form__input'); // Dawn uses fieldsets for each option
        
        fieldsets.forEach(fieldset => {
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (checkedRadio) {
                selectedOptions.push(checkedRadio.value);
            } else {
                const selectElement = fieldset.querySelector('select');
                if (selectElement) {
                    selectedOptions.push(selectElement.value);
                } else {
                    selectedOptions.push(null); // Should not happen if options are present
                }
            }
        });


        if (selectedOptions.some(opt => opt === null) || selectedOptions.length === 0) {
            // console.warn("ProductInfo: getSelectedVariant - Incomplete or no options selected.", selectedOptions);
            return null; 
        }

        return localVariantData.find(variant =>
            variant.options.length === selectedOptions.length &&
            variant.options.every((opt, index) => String(opt || '').trim().toLowerCase() === String(selectedOptions[index] || '').trim().toLowerCase())
        );
      }
      
      get productDataFromPage() {
        if (this._productDataFromPage) return this._productDataFromPage;
        // Ensure this uses the correct product ID, potentially this.dataset.productId if available on <product-info>
        const currentProductId = this.dataset.productId || (this.productForm?.form?.querySelector('input[name="product-id"]')?.value); // Fallback
        const productDataElement = document.getElementById('ProductData-' + currentProductId);

        if (productDataElement) {
            try {
                this._productDataFromPage = JSON.parse(productDataElement.textContent);
                return this._productDataFromPage;
            } catch(e) { console.error("ProductInfo: Error parsing ProductData JSON from page.", e); }
        }
        // console.warn("ProductInfo: ProductData JSON script not found on page.");
        return { options: [], variants: [] }; 
      }

      buildRequestUrlWithParams(url, optionValues, variantId = null, shouldFetchFullPage = false) {
        const params = new URLSearchParams();
        if (!shouldFetchFullPage) {
          params.append('section_id', this.sectionId); // Use getter for sectionId
        }
        if (variantId) {
            params.append('variant', variantId);
        }
        const queryString = params.toString();
        return `${url}${queryString ? `?${queryString}` : ''}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        const variantRadios = html.querySelector('variant-radios');
        const currentVariantSelectors = this.variantSelectors; 

        if (variantSelects && currentVariantSelectors && currentVariantSelectors.tagName.toUpperCase() === 'VARIANT-SELECTS') {
          HTMLUpdateUtility.viewTransition(currentVariantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        } else if (variantRadios && currentVariantSelectors && currentVariantSelectors.tagName.toUpperCase() === 'VARIANT-RADIOS') {
          HTMLUpdateUtility.viewTransition(currentVariantSelectors, variantRadios, this.preProcessHtmlCallbacks);
        }
      }
      
      updateVariantInputs(variantId) {
        this.querySelectorAll( 
          `form[id^="product-form-${this.dataset.section}"], form[id^="product-form-installment-${this.dataset.section}"]`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) { 
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(productUrl, variantId) {
        return; // Pagination/SEO: prevent ?variant= from being written to the URL
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${productUrl}${variantId ? `?variant=${variantId}` : ''}`);
      }

      // updateURL(url, variantId) {
      //   const shareButton = this.querySelector('share-button'); 
      //   shareButton?.updateUrl(
      //     `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
      //   );
      //   if (this.dataset.updateUrl === 'false') return;
      //   window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      // }

      setUnavailable() {
        const productFormComp = this.productForm; 
        if (productFormComp && typeof productFormComp.toggleSubmitButton === 'function') {
            productFormComp.toggleSubmitButton(true, window.variantStrings.unavailable);
        } else {
            // console.warn("[product-info.js] setUnavailable: productForm or toggleSubmitButton not available.");
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach((el) => el.classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId && variantFeaturedMediaId !== 0) return; // Allow 0 as a valid media ID
        const currentMediaGallery = this.querySelector('media-gallery'); 
        if (!currentMediaGallery) { /* console.warn(...) */ return; }
        const mediaGallerySourceContainer = html.querySelector(`media-gallery[data-section-id="${this.sectionId}"]`) || html.querySelector('media-gallery');
        if (!mediaGallerySourceContainer) { /* console.warn(...) */ return; }
        
        const mediaGallerySource = mediaGallerySourceContainer.querySelector('ul');
        const mediaGalleryDestination = currentMediaGallery.querySelector('ul');
        
        if (mediaGalleryDestination && mediaGallerySource) {
            // More robust media update (Dawn's refreshSourceData and diffing logic is complex,
            // this is a simpler placeholder. For full fidelity, you'd port Dawn's diffing logic.)
            // This simple replacement can cause image flickering or loss of scroll position.
             HTMLUpdateUtility.viewTransition(mediaGalleryDestination, mediaGallerySource, this.preProcessHtmlCallbacks, () => {
                currentMediaGallery.setActiveMedia?.(
                    `${this.dataset.section}-${variantFeaturedMediaId}`, true, true
                );
             });
        } else {
            currentMediaGallery.setActiveMedia?.(
              `${this.dataset.section}-${variantFeaturedMediaId}`, true
            );
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal[id="ProductModal-${this.sectionId}"] .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
         if (!this.quantityInput) return; 
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };
        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.quantityInput.min = min;
        if (max !== null && max >= min) { // Allow max to be 0 if valid (e.g. sold out)
          this.quantityInput.max = max;
        } else if (max === null) {
          this.quantityInput.removeAttribute('max');
        } else { // max is less than min, effectively making it unavailable
          this.quantityInput.max = min > 0 ? min -1 : 0; // or handle as error
        }
        this.quantityInput.value = min;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.quantityUpdate) {
            publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        // Corrected § to &
        return fetch(`${this.dataset.url}?variant=${currentVariantId}§ion_id=${this.dataset.originalSection || this.dataset.section}`) 
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.originalSection || this.dataset.section, html);
           })
          .catch((e) => console.error(e))
          .finally(() => spinner?.classList.add('hidden'));
      }

      updateQuantityRules(sectionIdFromFetch, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionIdFromFetch}`);
        if (!quantityFormUpdated) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
              else current.removeAttribute(attribute);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector('product-form'); 
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects, variant-radios');
      }
      
      get sectionId() { 
        return this.dataset.section; // The section ID of the <product-info> element itself
      }
    }
  );
}